import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Menu,
  X,
  ChevronDown,
  LogIn,
  Phone,
  LayoutDashboard,
  Server,
  Building2,
  User,
  LogOut,
  Layers,
  FileText,
  LifeBuoy,
  HelpCircle,
  Mail,
  Sparkles,
  ArrowRight,
  Smartphone,
  ExternalLink,
  Shield,
  ShieldAlert,
  Activity,
  Briefcase,
  Box,
} from "lucide-react";
import { useModals } from "../contexts/ModalContext";
import { useAuth } from "../contexts/AuthContext";
import api from "../api";
import Logo from "./Logo";
import HostingMegaMenu from "./HostingMegaMenu";
import WebdesignMegaMenu from "./WebdesignMegaMenu";
import CinematicServerSlider from "./CinematicServerSlider";

const companyLinks = [
  { label: "Über uns", href: "#ueber", icon: Building2, desc: "Philosophie & Schweizer Qualität" },
  { label: "Kontakt & Beratung", href: "#kontakt", icon: Mail, desc: "Direkter Draht zu unseren Experten" },
  { label: "Sicherheitszentrum", to: "/operations-center", icon: ShieldAlert, desc: "Cyber-Abwehr & DDoS-Status" },
  { label: "Support-Portal", to: "/support", icon: LifeBuoy, desc: "Tickets & technischer Kundendienst" },
  { label: "Häufige Fragen (FAQ)", href: "#faq", icon: HelpCircle, desc: "Antworten auf wichtige Fragen" },
];

const serviceLinks = [
  { label: "Webdesign & Redesign", href: "#leistungen" },
  { label: "Softwareentwicklung", href: "#leistungen" },
  { label: "SEO & Sichtbarkeit", href: "#leistungen" },
  { label: "Branding & Corporate Identity", href: "#leistungen" },
  { label: "Online-Marketing & Ads", href: "#leistungen" },
  { label: "IT-Strategie & Beratung", href: "#leistungen" },
];

export default function Header({ scrolled }) {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [hostingOpen, setHostingOpen] = useState(false);
  const [webdesignOpen, setWebdesignOpen] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [cinematic3dOpen, setCinematic3dOpen] = useState(false);

  // Mobile drawer accordions
  const [mobileHostingOpen, setMobileHostingOpen] = useState(false);
  const [mobileWebdesignOpen, setMobileWebdesignOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileCompanyOpen, setMobileCompanyOpen] = useState(false);

  const [phone, setPhone] = useState("+41 76 298 10 15");

  const { openQuote } = useModals();
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  const headerRef = useRef(null);
  const hostingTimeoutRef = useRef(null);
  const webdesignTimeoutRef = useRef(null);
  const companyTimeoutRef = useRef(null);
  const accountTimeoutRef = useRef(null);
  const servicesTimeoutRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    api.get("/site-settings")
      .then((res) => {
        if (mounted && res.data) {
          const num = res.data.headerPhone || res.data.contactPhone || "+41 76 298 10 15";
          setPhone(num);
        }
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  // Keyboard accessibility: ESC closes mega-menu and dropdowns
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setHostingOpen(false);
        setWebdesignOpen(false);
        setServicesOpen(false);
        setCompanyOpen(false);
        setAccountOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Click outside to close menus
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) {
        setHostingOpen(false);
        setWebdesignOpen(false);
        setServicesOpen(false);
        setCompanyOpen(false);
        setAccountOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const handleHostingMouseEnter = () => {
    if (hostingTimeoutRef.current) clearTimeout(hostingTimeoutRef.current);
    setHostingOpen(true);
    setWebdesignOpen(false);
    setServicesOpen(false);
    setCompanyOpen(false);
    setAccountOpen(false);
  };

  const handleHostingMouseLeave = () => {
    hostingTimeoutRef.current = setTimeout(() => {
      setHostingOpen(false);
    }, 180);
  };

  const handleWebdesignMouseEnter = () => {
    if (webdesignTimeoutRef.current) clearTimeout(webdesignTimeoutRef.current);
    setWebdesignOpen(true);
    setHostingOpen(false);
    setServicesOpen(false);
    setCompanyOpen(false);
    setAccountOpen(false);
  };

  const handleWebdesignMouseLeave = () => {
    webdesignTimeoutRef.current = setTimeout(() => {
      setWebdesignOpen(false);
    }, 180);
  };

  const handleCompanyMouseEnter = () => {
    if (companyTimeoutRef.current) clearTimeout(companyTimeoutRef.current);
    setCompanyOpen(true);
    setHostingOpen(false);
    setWebdesignOpen(false);
    setServicesOpen(false);
    setAccountOpen(false);
  };

  const handleCompanyMouseLeave = () => {
    companyTimeoutRef.current = setTimeout(() => {
      setCompanyOpen(false);
    }, 180);
  };

  const handleAccountMouseEnter = () => {
    if (accountTimeoutRef.current) clearTimeout(accountTimeoutRef.current);
    setAccountOpen(true);
    setHostingOpen(false);
    setWebdesignOpen(false);
    setServicesOpen(false);
    setCompanyOpen(false);
  };

  const handleAccountMouseLeave = () => {
    accountTimeoutRef.current = setTimeout(() => {
      setAccountOpen(false);
    }, 180);
  };

  const handleNav = (e, href) => {
    if (!href?.startsWith("#")) return;
    e.preventDefault();
    const id = href.slice(1);
    if (id === "top") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      const el = document.getElementById(id);
      if (el) {
        const headerHeight = headerRef.current?.offsetHeight || 80;
        const top = el.getBoundingClientRect().top + window.scrollY - headerHeight - 14;
        window.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
      }
    }
    setOpen(false);
    setServicesOpen(false);
    setHostingOpen(false);
    setWebdesignOpen(false);
    setCompanyOpen(false);
    setAccountOpen(false);
  };

  const dashboardUrl = isAdmin ? "/admin" : "/dashboard";
  const cleanTel = `tel:${phone.replace(/\s+/g, "")}`;

  return (
    <header
      ref={headerRef}
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-200 bg-[#07090E] border-b border-white/10 ${
        scrolled
          ? "shadow-[0_12px_35px_rgba(0,0,0,0.75)]"
          : "shadow-[0_4px_20px_rgba(0,0,0,0.5)]"
      }`}
    >
      {/* ============================================================== */}
      {/* 1. TOP ANNOUNCEMENT BAR: Sleek, non-overflowing Swiss banner    */}
      {/* ============================================================== */}
      <div className="w-full bg-[#05070B] border-b border-white/10 overflow-hidden">
        <div className="max-w-7xl mx-auto px-3 sm:px-6">
          {/* Mobile view (< sm) */}
          <div className="sm:hidden flex items-center justify-between h-8 text-[11px]">
            <div className="flex items-center gap-1.5 truncate">
              <span className="bg-[#E63946] text-white px-1.5 py-0.2 rounded font-black text-[9px] uppercase tracking-wider shrink-0">
                APP
              </span>
              <span className="text-slate-300 truncate">
                Website als iOS & Android App
              </span>
            </div>
            <Link
              to="/webdesign#webview-app"
              className="text-[#D4AF37] hover:text-white font-bold shrink-0 ml-2 flex items-center gap-1"
            >
              <span>ab CHF 299.-</span>
              <ArrowRight size={11} />
            </Link>
          </div>

          {/* Desktop & Tablet view (sm+) */}
          <div className="hidden sm:flex items-center justify-center gap-2.5 h-8 text-xs">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#E63946]/20 text-[#E63946] font-black text-[10px] uppercase tracking-wider border border-[#E63946]/35 shrink-0">
              <Sparkles size={11} className="animate-pulse" /> NEU
            </span>
            <span className="text-slate-300">
              Ihre Website als <strong className="text-white">iOS & Android App (Webview)</strong> – Native Performance & Push
            </span>
            <span className="font-mono font-bold text-[#D4AF37] bg-white/5 px-2 py-0.5 rounded-md border border-white/10 text-[11px] shrink-0">
              Ab CHF 299.-
            </span>
            <Link
              to="/webdesign#webview-app"
              className="inline-flex items-center gap-1 font-bold text-white hover:text-[#E63946] transition underline text-xs shrink-0 ml-1"
            >
              <span>App anfragen</span>
              <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      </div>

      {/* ============================================================== */}
      {/* 2. MAIN HEADER ROW                                             */}
      {/* ============================================================== */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-2.5 sm:py-3 flex items-center justify-between gap-4">
        {/* Brand Logo */}
        <a
          href="#top"
          onClick={(e) => handleNav(e, "#top")}
          className="shrink-0 transition-transform hover:scale-105 select-none"
          aria-label="RedWORK Startseite"
        >
          <Logo size="md" />
        </a>

        {/* ============================================================ */}
        {/* DESKTOP NAVIGATION CAPSULE (Clean, unified typography)       */}
        {/* ============================================================ */}
        <nav className="hidden lg:flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.04] p-1 shadow-inner backdrop-blur-md">
          {/* 1. Start */}
          <a
            href="#top"
            onClick={(e) => handleNav(e, "#top")}
            className="rounded-full px-3.5 py-1.5 text-[13px] font-medium text-slate-300 hover:text-white hover:bg-white/[0.08] transition-all duration-200"
          >
            Start
          </a>

          {/* 2. Webhosting Mega Menu Trigger */}
          <div
            className="relative"
            onMouseEnter={handleHostingMouseEnter}
            onMouseLeave={handleHostingMouseLeave}
          >
            <button
              type="button"
              onClick={() => {
                setHostingOpen((v) => !v);
                setWebdesignOpen(false);
                setServicesOpen(false);
                setCompanyOpen(false);
              }}
              className={`group flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-medium transition-all duration-200 cursor-pointer ${
                hostingOpen
                  ? "bg-white/[0.12] text-white shadow-sm"
                  : "text-slate-300 hover:text-white hover:bg-white/[0.08]"
              }`}
              aria-expanded={hostingOpen}
            >
              <Server size={13} className={`transition-colors ${hostingOpen ? "text-[#E63946]" : "text-slate-400 group-hover:text-[#E63946]"}`} />
              <span>Webhosting</span>
              <ChevronDown
                size={13}
                className={`transition-transform duration-200 text-slate-400 group-hover:text-white ${
                  hostingOpen ? "rotate-180 text-white" : ""
                }`}
              />
            </button>
          </div>

          {/* 3. Webdesign Mega Menu Trigger */}
          <div
            className="relative"
            onMouseEnter={handleWebdesignMouseEnter}
            onMouseLeave={handleWebdesignMouseLeave}
          >
            <button
              type="button"
              onClick={() => {
                setWebdesignOpen((v) => !v);
                setHostingOpen(false);
                setServicesOpen(false);
                setCompanyOpen(false);
              }}
              className={`group flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-medium transition-all duration-200 cursor-pointer ${
                webdesignOpen
                  ? "bg-white/[0.12] text-white shadow-sm"
                  : "text-slate-300 hover:text-white hover:bg-white/[0.08]"
              }`}
              aria-expanded={webdesignOpen}
            >
              <Sparkles size={13} className={`transition-colors ${webdesignOpen ? "text-[#FF7A00]" : "text-slate-400 group-hover:text-[#FF7A00]"}`} />
              <span>Webdesign</span>
              <ChevronDown
                size={13}
                className={`transition-transform duration-200 text-slate-400 group-hover:text-white ${
                  webdesignOpen ? "rotate-180 text-white" : ""
                }`}
              />
            </button>
          </div>

          {/* 4. Leistungen Dropdown */}
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setServicesOpen((v) => !v);
                setHostingOpen(false);
                setWebdesignOpen(false);
                setCompanyOpen(false);
              }}
              className={`group flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-medium transition-all duration-200 ${
                servicesOpen
                  ? "bg-white/[0.12] text-white shadow-sm"
                  : "text-slate-300 hover:text-white hover:bg-white/[0.08]"
              }`}
            >
              <span>Leistungen</span>
              <ChevronDown
                size={13}
                className={`transition-transform duration-200 text-slate-400 group-hover:text-white ${
                  servicesOpen ? "rotate-180 text-white" : ""
                }`}
              />
            </button>

            {servicesOpen && (
              <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-72 overflow-hidden rounded-2xl border border-white/10 bg-[#07090E] p-2 shadow-2xl animate-in fade-in zoom-in-95 duration-150">
                <div className="px-3 py-1.5 text-[10px] font-black uppercase tracking-wider text-slate-400 border-b border-white/5 mb-1">
                  Agentur-Leistungen
                </div>
                {serviceLinks.map((service) => (
                  <a
                    key={service.label}
                    href={service.href}
                    onClick={(e) => handleNav(e, service.href)}
                    className="block rounded-xl px-3 py-2 text-xs font-semibold text-slate-200 hover:text-white hover:bg-white/10 transition"
                  >
                    {service.label}
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* 5. Projekte Link */}
          <a
            href="#projekte"
            onClick={(e) => handleNav(e, "#projekte")}
            className="rounded-full px-3.5 py-1.5 text-[13px] font-medium text-slate-300 hover:text-white hover:bg-white/[0.08] transition-all duration-200"
          >
            Projekte
          </a>

          {/* 6. Operations Center (Live Cyber Defense) */}
          <Link
            to="/operations-center"
            className="group relative flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-semibold text-slate-200 hover:text-red-400 hover:bg-red-500/10 transition-all border border-transparent hover:border-red-500/25"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
            </span>
            <span>Sicherheitszentrum</span>
            <span className="rounded bg-red-500/20 px-1 py-0.2 text-[8px] font-black text-red-400 uppercase tracking-widest">
              LIVE
            </span>
          </Link>

          {/* 6. Unternehmen Dropdown */}
          <div
            className="relative"
            onMouseEnter={handleCompanyMouseEnter}
            onMouseLeave={handleCompanyMouseLeave}
          >
            <button
              type="button"
              onClick={() => {
                setCompanyOpen((v) => !v);
                setHostingOpen(false);
                setWebdesignOpen(false);
                setServicesOpen(false);
              }}
              className={`group flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-medium transition-all duration-200 ${
                companyOpen
                  ? "bg-white/[0.12] text-white shadow-sm"
                  : "text-slate-300 hover:text-white hover:bg-white/[0.08]"
              }`}
            >
              <span>Unternehmen</span>
              <ChevronDown
                size={13}
                className={`transition-transform duration-200 text-slate-400 group-hover:text-white ${
                  companyOpen ? "rotate-180 text-white" : ""
                }`}
              />
            </button>

            {companyOpen && (
              <div
                className="absolute right-0 top-full mt-2 w-76 overflow-hidden rounded-2xl border border-white/10 bg-[#07090E] p-2 shadow-2xl animate-in fade-in zoom-in-95 duration-150"
                onMouseEnter={handleCompanyMouseEnter}
                onMouseLeave={handleCompanyMouseLeave}
              >
                {companyLinks.map((item) => {
                  const Icon = item.icon;
                  const content = (
                    <div className="flex items-start gap-3 rounded-xl p-2 hover:bg-white/10 transition group">
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white/5 text-[#D4AF37] group-hover:bg-[#D4AF37]/20 transition mt-0.5">
                        <Icon size={14} />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-white group-hover:text-[#D4AF37] transition">
                          {item.label}
                        </p>
                        <p className="text-[11px] text-slate-400 mt-0.5">{item.desc}</p>
                      </div>
                    </div>
                  );
                  return item.to ? (
                    <Link key={item.label} to={item.to} onClick={() => setCompanyOpen(false)}>
                      {content}
                    </Link>
                  ) : (
                    <a key={item.label} href={item.href} onClick={(e) => handleNav(e, item.href)}>
                      {content}
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        </nav>

        {/* ============================================================ */}
        {/* RIGHT AREA: Phone Pill, Login/Account, Primary CTA           */}
        {/* ============================================================ */}
        <div className="hidden lg:flex items-center gap-2.5 shrink-0">
          {/* Phone Hotline Link */}
          <a
            href={cleanTel}
            className="hidden xl:flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-3.5 py-1.5 text-xs font-medium text-slate-300 hover:text-[#FFC107] hover:border-white/20 transition-all font-mono"
            title="Telefonische Beratung"
          >
            <Phone size={13} className="text-[#FFC107]" />
            <span>{phone}</span>
          </a>

          {/* User Account / Login */}
          {user ? (
            <div
              className="relative"
              onMouseEnter={handleAccountMouseEnter}
              onMouseLeave={handleAccountMouseLeave}
            >
              <button
                type="button"
                onClick={() => setAccountOpen((v) => !v)}
                className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                  accountOpen
                    ? "border-[#D4AF37] bg-white/10 text-white"
                    : "border-white/10 bg-white/[0.04] text-white hover:bg-white/10"
                }`}
              >
                <div className="flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-tr from-[#E63946] to-[#FF7A00] text-[10px] font-bold text-white">
                  {(user.firstName || user.name || "K").charAt(0).toUpperCase()}
                </div>
                <span>Mein Konto</span>
                <ChevronDown size={12} className={`transition duration-200 ${accountOpen ? "rotate-180 text-[#D4AF37]" : ""}`} />
              </button>

              {accountOpen && (
                <div
                  className="absolute right-0 top-full mt-2 w-64 overflow-hidden rounded-2xl border border-white/10 bg-[#07090E] p-2 shadow-2xl animate-in fade-in zoom-in-95 duration-150"
                  onMouseEnter={handleAccountMouseEnter}
                  onMouseLeave={handleAccountMouseLeave}
                >
                  <div className="px-3 py-2 border-b border-white/10 mb-1">
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider">Angemeldet als</p>
                    <p className="text-xs font-bold text-white truncate">
                      {user.firstName} {user.lastName}
                    </p>
                    <p className="text-[10px] text-slate-400 font-mono truncate">{user.email}</p>
                  </div>

                  <Link
                    to={dashboardUrl}
                    onClick={() => setAccountOpen(false)}
                    className="flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold text-white hover:bg-white/10 transition"
                  >
                    <LayoutDashboard size={14} className="text-[#FF7A00]" />
                    <span>Dashboard</span>
                  </Link>
                  <Link
                    to="/dashboard?tab=services"
                    onClick={() => setAccountOpen(false)}
                    className="flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold text-white hover:bg-white/10 transition"
                  >
                    <Layers size={14} className="text-blue-400" />
                    <span>Meine Services</span>
                  </Link>
                  <Link
                    to="/dashboard?tab=servers"
                    onClick={() => setAccountOpen(false)}
                    className="flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold text-white hover:bg-white/10 transition"
                  >
                    <Server size={14} className="text-[#D4AF37]" />
                    <span>Hosting (cPanel)</span>
                  </Link>
                  <Link
                    to="/dashboard?tab=invoices"
                    onClick={() => setAccountOpen(false)}
                    className="flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold text-white hover:bg-white/10 transition"
                  >
                    <FileText size={14} className="text-emerald-400" />
                    <span>Rechnungen</span>
                  </Link>
                  <Link
                    to="/support"
                    onClick={() => setAccountOpen(false)}
                    className="flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold text-white hover:bg-white/10 transition"
                  >
                    <LifeBuoy size={14} className="text-purple-400" />
                    <span>Support</span>
                  </Link>

                  <div className="my-1 border-t border-white/10" />

                  <button
                    onClick={() => {
                      logout();
                      setAccountOpen(false);
                      navigate("/");
                    }}
                    className="flex w-full items-center gap-2 rounded-xl px-3 py-1.5 text-xs font-semibold text-red-400 hover:bg-red-500/10 transition"
                  >
                    <LogOut size={14} />
                    <span>Abmelden</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link
              to="/login"
              className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-3.5 py-1.5 text-xs font-semibold text-slate-200 hover:text-white hover:bg-white/10 transition"
            >
              <LogIn size={13} />
              <span>Login</span>
            </Link>
          )}

          {/* Primary CTA: Angebot einholen (Never cut off, crisp design) */}
          <button
            onClick={openQuote}
            className="group relative overflow-hidden rounded-full bg-[#E63946] hover:bg-[#d62839] px-4.5 py-1.5 text-xs font-bold text-white shadow-[0_0_15px_rgba(230,57,70,0.35)] hover:shadow-[0_0_22px_rgba(230,57,70,0.6)] transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] flex items-center gap-1.5 shrink-0"
            aria-label="Kostenloses Angebot einholen"
          >
            <span>Angebot</span>
            <ArrowRight size={13} className="transition-transform duration-200 group-hover:translate-x-0.5" />
          </button>
        </div>

        {/* ============================================================ */}
        {/* MOBILE CONTROLS (Right side: Call Button + Menu Toggle)      */}
        {/* ============================================================ */}
        <div className="flex lg:hidden items-center gap-2 shrink-0">
          <a
            href={cleanTel}
            className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/[0.06] border border-white/10 text-[#FFC107] hover:bg-white/10 transition"
            aria-label="Direkt anrufen"
          >
            <Phone size={15} />
          </a>
          <button
            className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/[0.06] border border-white/10 text-white hover:bg-white/10 transition"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menü öffnen"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* ============================================================== */}
      {/* 3. DESKTOP MEGA MENUS (Anchored directly beneath header bar)   */}
      {/* ============================================================== */}
      {hostingOpen && (
        <div
          className="hidden lg:block absolute left-1/2 -translate-x-1/2 top-full mt-1.5 z-50 pointer-events-auto"
          onMouseEnter={handleHostingMouseEnter}
          onMouseLeave={handleHostingMouseLeave}
        >
          <HostingMegaMenu onClose={() => setHostingOpen(false)} isMobile={false} />
        </div>
      )}

      {webdesignOpen && (
        <div
          className="hidden lg:block absolute left-1/2 -translate-x-1/2 top-full mt-1.5 z-50 pointer-events-auto"
          onMouseEnter={handleWebdesignMouseEnter}
          onMouseLeave={handleWebdesignMouseLeave}
        >
          <WebdesignMegaMenu onClose={() => setWebdesignOpen(false)} isMobile={false} />
        </div>
      )}

      {/* ============================================================== */}
      {/* 4. MOBILE SLIDE-DOWN DRAWER (Clean Swiss mobile design)        */}
      {/* ============================================================== */}
      {open && (
        <div className="lg:hidden border-t border-white/10 bg-[#07090E] px-4 py-4 shadow-2xl max-h-[calc(100vh-80px)] overflow-y-auto">
          <div className="space-y-2 max-w-xl mx-auto">
            {/* Quick Action Bar: Call Hotline & Login/Account */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              <a
                href={cleanTel}
                className="flex items-center justify-center gap-1.5 rounded-xl border border-white/10 bg-white/[0.04] p-2.5 text-xs font-bold text-white hover:bg-white/10 transition font-mono"
              >
                <Phone size={14} className="text-[#FFC107]" />
                <span className="truncate">{phone}</span>
              </a>

              {user ? (
                <Link
                  to={dashboardUrl}
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-center gap-1.5 rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/10 p-2.5 text-xs font-bold text-white hover:bg-[#D4AF37]/20 transition"
                >
                  <User size={14} className="text-[#D4AF37]" />
                  <span className="truncate">Konto ({user.firstName || "Kunde"})</span>
                </Link>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-center gap-1.5 rounded-xl border border-white/10 bg-white/[0.04] p-2.5 text-xs font-bold text-white hover:bg-white/10 transition"
                >
                  <LogIn size={14} />
                  <span>Kunden-Login</span>
                </Link>
              )}
            </div>

            {/* Start Link */}
            <a
              href="#top"
              onClick={(e) => handleNav(e, "#top")}
              className="flex items-center justify-between p-3 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/5 text-sm font-bold text-white transition"
            >
              <span>Startseite</span>
              <ArrowRight size={14} className="text-slate-500" />
            </a>

            {/* Mobile Accordion: Webhosting & Cloud */}
            <div className="rounded-xl border border-white/5 bg-white/[0.03] overflow-hidden">
              <button
                type="button"
                onClick={() => setMobileHostingOpen((v) => !v)}
                className="w-full flex items-center justify-between p-3 text-left transition hover:bg-white/[0.04]"
              >
                <div className="flex items-center gap-2.5">
                  <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#E63946]/15 text-[#E63946] border border-[#E63946]/25">
                    <Server size={14} />
                  </div>
                  <span className="text-sm font-bold text-white">Webhosting & Cloud</span>
                  <span className="text-[10px] font-bold text-emerald-400 bg-emerald-500/15 px-2 py-0.5 rounded-full border border-emerald-500/25">
                    7 Tarife
                  </span>
                </div>
                <ChevronDown
                  size={16}
                  className={`text-slate-400 transition-transform duration-200 ${
                    mobileHostingOpen ? "rotate-180 text-white" : ""
                  }`}
                />
              </button>
              {mobileHostingOpen && (
                <div className="px-3 pb-3 border-t border-white/5">
                  <HostingMegaMenu onClose={() => setOpen(false)} isMobile={true} />
                </div>
              )}
            </div>

            {/* Mobile Accordion: Webdesign & Apps */}
            <div className="rounded-xl border border-white/5 bg-white/[0.03] overflow-hidden">
              <button
                type="button"
                onClick={() => setMobileWebdesignOpen((v) => !v)}
                className="w-full flex items-center justify-between p-3 text-left transition hover:bg-white/[0.04]"
              >
                <div className="flex items-center gap-2.5">
                  <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#FF7A00]/15 text-[#FF7A00] border border-[#FF7A00]/25">
                    <Sparkles size={14} />
                  </div>
                  <span className="text-sm font-bold text-white">Webdesign & Apps</span>
                  <span className="text-[10px] font-bold text-amber-400 bg-amber-500/15 px-2 py-0.5 rounded-full border border-amber-500/25">
                    7 Pakete
                  </span>
                </div>
                <ChevronDown
                  size={16}
                  className={`text-slate-400 transition-transform duration-200 ${
                    mobileWebdesignOpen ? "rotate-180 text-white" : ""
                  }`}
                />
              </button>
              {mobileWebdesignOpen && (
                <div className="px-3 pb-3 border-t border-white/5">
                  <WebdesignMegaMenu onClose={() => setOpen(false)} isMobile={true} />
                </div>
              )}
            </div>

            {/* Mobile Accordion: Leistungen */}
            <div className="rounded-xl border border-white/5 bg-white/[0.03] overflow-hidden">
              <button
                type="button"
                onClick={() => setMobileServicesOpen((v) => !v)}
                className="w-full flex items-center justify-between p-3 text-left transition hover:bg-white/[0.04]"
              >
                <div className="flex items-center gap-2.5">
                  <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-500/15 text-blue-400 border border-blue-500/25">
                    <Briefcase size={14} />
                  </div>
                  <span className="text-sm font-bold text-white">Leistungen</span>
                </div>
                <ChevronDown
                  size={16}
                  className={`text-slate-400 transition-transform duration-200 ${
                    mobileServicesOpen ? "rotate-180 text-white" : ""
                  }`}
                />
              </button>
              {mobileServicesOpen && (
                <div className="px-3 pb-3 pt-1 border-t border-white/5 space-y-1">
                  {serviceLinks.map((service) => (
                    <a
                      key={service.label}
                      href={service.href}
                      onClick={(e) => handleNav(e, service.href)}
                      className="block p-2 rounded-lg text-xs font-semibold text-slate-300 hover:text-white hover:bg-white/5 transition"
                    >
                      {service.label}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Projekte Link */}
            <a
              href="#projekte"
              onClick={(e) => handleNav(e, "#projekte")}
              className="flex items-center justify-between p-3 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/5 text-sm font-bold text-white transition"
            >
              <span>Projekte & Referenzen</span>
              <ArrowRight size={14} className="text-slate-500" />
            </a>

            {/* Mobile Operations Center */}
            <Link
              to="/operations-center"
              onClick={() => setOpen(false)}
              className="flex items-center justify-between p-3 rounded-xl border border-red-500/30 bg-red-500/10 text-sm font-bold text-red-400 hover:bg-red-500/20 transition"
            >
              <div className="flex items-center gap-2.5">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                </span>
                <span>Sicherheitszentrum</span>
              </div>
              <span className="rounded bg-red-500/20 px-1.5 py-0.5 text-[9px] font-black text-red-400 uppercase tracking-wider">
                LIVE TELEMETRIE
              </span>
            </Link>

            {/* Mobile Accordion: Unternehmen & Support */}
            <div className="rounded-xl border border-white/5 bg-white/[0.03] overflow-hidden">
              <button
                type="button"
                onClick={() => setMobileCompanyOpen((v) => !v)}
                className="w-full flex items-center justify-between p-3 text-left transition hover:bg-white/[0.04]"
              >
                <div className="flex items-center gap-2.5">
                  <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/25">
                    <Building2 size={14} />
                  </div>
                  <span className="text-sm font-bold text-white">Unternehmen & Support</span>
                </div>
                <ChevronDown
                  size={16}
                  className={`text-slate-400 transition-transform duration-200 ${
                    mobileCompanyOpen ? "rotate-180 text-white" : ""
                  }`}
                />
              </button>
              {mobileCompanyOpen && (
                <div className="px-3 pb-3 pt-1 border-t border-white/5 space-y-1">
                  {companyLinks.map((item) => {
                    const content = (
                      <div className="p-2 rounded-lg text-xs font-semibold text-slate-300 hover:text-white hover:bg-white/5 transition flex items-center justify-between">
                        <span>{item.label}</span>
                        <span className="text-[10px] text-slate-500">{item.desc}</span>
                      </div>
                    );
                    return item.to ? (
                      <Link key={item.label} to={item.to} onClick={() => setOpen(false)}>
                        {content}
                      </Link>
                    ) : (
                      <a key={item.label} href={item.href} onClick={(e) => handleNav(e, item.href)}>
                        {content}
                      </a>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Primary Action Button: Kostenloses Angebot anfragen */}
            <div className="pt-2">
              <button
                onClick={() => {
                  openQuote();
                  setOpen(false);
                }}
                className="w-full py-3.5 rounded-xl bg-gradient-to-r from-[#E63946] to-[#ff4d5a] text-sm font-black text-white shadow-lg shadow-[#E63946]/30 flex items-center justify-center gap-2 active:scale-95 transition"
              >
                <span>Kostenloses Angebot anfragen</span>
                <ArrowRight size={15} />
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ============================================================== */}
      {/* 3D CINEMATIC SERVER MODAL SHOWCASE                             */}
      {/* ============================================================== */}
      {cinematic3dOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-xl p-3 sm:p-6 overflow-y-auto animate-in fade-in duration-200"
          onClick={() => setCinematic3dOpen(false)}
        >
          <div
            className="relative w-full max-w-6xl rounded-3xl bg-[#07090F] border border-cyan-500/30 p-4 sm:p-8 shadow-[0_25px_100px_rgba(0,240,255,0.2)] max-h-[95vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header bar */}
            <div className="flex items-center justify-between pb-4 mb-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-cyan-500/20 text-cyan-400 border border-cyan-500/40">
                  <Box size={20} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-base sm:text-lg font-black text-white">
                      3D Cinematic Server-Rack Architektur
                    </h3>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
                      Tier-IV Zürich
                    </span>
                  </div>
                  <p className="text-xs text-slate-400">
                    Interaktive Blade-Inspektion • AMD EPYC™ 9654 • 10 Gbit/s Uplink • Hydro-Cooled
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setCinematic3dOpen(false)}
                className="p-2 rounded-xl bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition cursor-pointer"
                aria-label="Schliessen"
              >
                <X size={20} />
              </button>
            </div>

            {/* Cinematic 3D Slider */}
            <CinematicServerSlider
              onScrollToPackages={() => {
                setCinematic3dOpen(false);
                navigate("/webhosting#produkte");
              }}
            />
          </div>
        </div>
      )}
    </header>
  );
}
